import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import './Profile.css';

const fields = [
  { key: 'phone',         label: 'Mobile No',      icon: '📞', color: '#dbeafe', iconColor: '#3b82f6' },
  { key: 'email',         label: 'Email',           icon: '✉️',  color: '#dbeafe', iconColor: '#3b82f6' },
  { key: 'address',       label: 'Address',         icon: '📍', color: '#dbeafe', iconColor: '#3b82f6' },
  { key: 'occupation',    label: 'Occupation',      icon: '💼', color: '#dbeafe', iconColor: '#3b82f6' },
  { key: 'gender',        label: 'Gender',          icon: '⚧️', color: '#dbeafe', iconColor: '#3b82f6' },
  { key: 'dob',           label: 'Date of Birth',   icon: '📅', color: '#dbeafe', iconColor: '#3b82f6' },
  { key: 'maritalStatus', label: 'Marital Status',  icon: '❤️', color: '#fce7f3', iconColor: '#ec4899' },
];

export default function CreateProfile() {
  const navigate = useNavigate();
  const { user, login, updateProfile } = useAuth();
  const [editing, setEditing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '', email: '', address: '', occupation: '',
    gender: '', dob: '', maritalStatus: '',
  });

  const [verifyingEmail, setVerifyingEmail] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [showOtpPopup, setShowOtpPopup] = useState(false);
  const [emailOtp, setEmailOtp] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);

  useEffect(() => {
    api.get('/api/users/profile').then(res => {
      const u = res.data.data;
      setForm({
        name:          u.name          || '',
        email:         u.email         || '',
        address:       u.address       || '',
        occupation:    u.occupation    || '',
        gender:        u.gender        || '',
        dob:           u.dob           || '',
        maritalStatus: u.maritalStatus || '',
      });
      setEmailVerified(u.emailVerified || false);
      if (u.avatar) setPreview(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/${u.avatar}`);
    }).catch(() => {
      if (user) {
        setForm({ name: user.name || '', email: user.email || '', address: user.address || '', occupation: user.occupation || '', gender: user.gender || '', dob: user.dob || '', maritalStatus: user.maritalStatus || '' });
        if (user.avatar) setPreview(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/${user.avatar}`);
      }
    });
  }, [user]);

  const handleVerifyEmail = async () => {
    setVerifyingEmail(true);
    try {
      await api.post('/api/auth/send-verify-email');
      toast.success('OTP sent to your email!');
      setShowOtpPopup(true);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send OTP');
    } finally {
      setVerifyingEmail(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setOtpLoading(true);
    try {
      await api.post('/api/auth/verify-email-otp', { otp: emailOtp });
      toast.success('Email verified successfully!');
      setEmailVerified(true);
      setShowOtpPopup(false);
      setEmailOtp('');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid OTP');
    } finally {
      setOtpLoading(false);
    }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAvatarFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const formData = new FormData();
      Object.keys(form).forEach(k => formData.append(k, form[k]));
      if (avatarFile) formData.append('avatar', avatarFile);
      await updateProfile(formData);
      setEditing(false);
    } catch (err) {
      alert(err.response?.data?.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const initials = form.name ? form.name.substring(0, 2).toUpperCase() : '👤';

  // ── Edit Page ──
  if (editing) {
    return (
      <div className="profile-page">
        <div className="container">
          <div className="profile-edit-card">
            <div className="profile-edit-header">
              <button className="profile-back-arrow" onClick={() => setEditing(false)}>◀</button>
              <span><strong>My Account</strong> / Edit Personal Details</span>
            </div>
            <form onSubmit={handleUpdate} className="profile-edit-form">
              <div className="profile-edit-row3">
                <div className="pfe-group">
                  <label>Name</label>
                  <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Name" />
                </div>
                <div className="pfe-group">
                  <label>Mobile Number</label>
                  <input value={user?.phone || ''} readOnly placeholder="Mobile Number" />
                </div>
                <div className="pfe-group">
                  <label>Email Address</label>
                  <input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="Email Address" />
                </div>
              </div>
              <div className="profile-edit-row2">
                <div className="pfe-group">
                  <label>Occupation</label>
                  <input value={form.occupation} onChange={e => set('occupation', e.target.value)} placeholder="Occupation" />
                </div>
                <div className="pfe-group">
                  <label>Address</label>
                  <input value={form.address} onChange={e => set('address', e.target.value)} placeholder="Address" />
                </div>
              </div>
              <div className="profile-edit-row3">
                <div className="pfe-group">
                  <label className="pfe-label-green">Gender <span>*</span></label>
                  <select value={form.gender} onChange={e => set('gender', e.target.value)}>
                    <option value="">Select Gender</option>
                    <option>Male</option>
                    <option>Female</option>
                    <option>Other</option>
                  </select>
                </div>
                <div className="pfe-group">
                  <label className="pfe-label-green">Date of Birth</label>
                  <input type="date" value={form.dob} onChange={e => set('dob', e.target.value)} />
                </div>
                <div className="pfe-group">
                  <label className="pfe-label-green">Marital Status</label>
                  <select value={form.maritalStatus} onChange={e => set('maritalStatus', e.target.value)}>
                    <option value="">Select Marital Status</option>
                    <option>Single</option>
                    <option>Married</option>
                    <option>Divorced</option>
                    <option>Widowed</option>
                  </select>
                </div>
              </div>
              <div className="pfe-group">
                <label>Profile Photo</label>
                <input type="file" accept="image/*" onChange={handleImageChange} />
              </div>
              <div className="profile-edit-actions">
                <button type="submit" className="btn-update" disabled={saving}>{saving ? 'Saving...' : '✓ Update'}</button>
                <button type="button" className="btn-cancel" onClick={() => setEditing(false)}>✕ Cancel</button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // ── View Page ──
  return (
    <div className="profile-page">
      <div className="container">
        {/* Banner */}
        <div className="profile-banner">
          <div className="profile-avatar-wrap">
            <div className="profile-avatar">
              {preview ? <img src={preview} alt="avatar" /> : <span>{initials}</span>}
            </div>
            <label className="avatar-edit-btn" htmlFor="avatar-input">📷</label>
            <input id="avatar-input" type="file" accept="image/*" onChange={handleImageChange} hidden />
          </div>
          <h2 className="profile-banner-name">{form.name || 'Your Name'}</h2>
        </div>

        {/* Info Card */}
        <div className="profile-info-card">
          {fields.map((f) => (
            <div key={f.key} className="profile-info-row">
              <div className="profile-info-left">
                <div className="profile-info-icon" style={{ background: f.color }}>
                  <span>{f.icon}</span>
                </div>
                <span className="profile-info-label">{f.label}</span>
              </div>
              <div className="profile-info-value">
                {f.key === 'phone' ? (user?.phone || '—') : (form[f.key] || '')}
                {f.key === 'email' && form.email && (
                  emailVerified
                    ? <span style={{ color: '#16a34a', fontSize: 12, fontWeight: 600 }}>✅ Verified</span>
                    : <button className="verify-email-btn" onClick={handleVerifyEmail} disabled={verifyingEmail}>
                        {verifyingEmail ? 'Sending...' : 'Verify Email'}
                      </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="profile-action-btns">
          <button className="btn-edit-profile" onClick={() => setEditing(true)}>✏️ Edit Profile</button>
          <button className="btn-delete-account" onClick={() => { if(window.confirm('Delete account?')) alert('Coming soon'); }}>Delete Account</button>
        </div>
      </div>

      {/* OTP Popup */}
      {showOtpPopup && (
        <div className="modal-overlay" onClick={() => setShowOtpPopup(false)}>
          <div style={{ background: '#fff', borderRadius: 16, padding: '32px 28px', width: 320, textAlign: 'center' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>✉️</div>
            <h3 style={{ margin: '0 0 6px', color: '#1e293b' }}>Enter OTP</h3>
            <p style={{ color: '#64748b', fontSize: 13, margin: '0 0 20px' }}>OTP sent to <b>{form.email}</b></p>
            <form onSubmit={handleOtpSubmit}>
              <input
                type="text"
                maxLength={6}
                placeholder="Enter 6-digit OTP"
                value={emailOtp}
                onChange={e => setEmailOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 20, letterSpacing: 8, textAlign: 'center', outline: 'none', boxSizing: 'border-box', marginBottom: 16 }}
                autoFocus
              />
              <button type="submit" disabled={otpLoading || emailOtp.length !== 6}
                style={{ width: '100%', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 10, padding: '12px', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
                {otpLoading ? 'Verifying...' : 'Verify OTP'}
              </button>
              <button type="button" onClick={() => { setShowOtpPopup(false); setEmailOtp(''); }}
                style={{ width: '100%', marginTop: 8, background: 'transparent', color: '#64748b', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px', fontSize: 14, cursor: 'pointer' }}>
                Cancel
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
